import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { monthsData, RevenueType, yearData } from "@/constants/dashboardData";
import { GET } from "@/services/api";
import { API_ROUTES } from "@/services/environment";
import { useAppSelector } from "@/hooks/hook";
import { FaCircle } from "react-icons/fa";
import { convertCentsToDollars } from "@thdinc/hpf-shared";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);
const options = {
  plugins: {
    legend: {
      display: false,
    },
  },
};
interface ChartDataset {
  label: string;
  data: number[];
  borderColor: string;
  backgroundColor: string;
  fill: boolean;
  tension: number;
  pointRadius: number;
  pointHoverRadius: number;
}

interface ChartData {
  labels: string[];
  datasets: ChartDataset[];
}
const RevenueChart = () => {
  const { getChartRevenue } = API_ROUTES;
  const { refreshToken } = useAppSelector((store) => store.user);
  const { currentBusiness } = useAppSelector((store) => store.business);
  const [type, setType] = useState<string>("Service");
  const [selectedMonth, setSelectedMonth] = useState<number | string>(
    new Date().getMonth()
  );
  const [monthRang, setMonthRang] = useState<any[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | string>(
    new Date().getFullYear()
  );
  const [data, setData] = useState<ChartData>({
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    datasets: [
      {
        label: "Revenue",
        data: [],
        borderColor: "orange",
        backgroundColor: "rgba(255, 165, 0, 0.3)",
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
      {
        label: "Cost",
        data: [],
        borderColor: "cyan",
        backgroundColor: "rgba(0, 255, 255, 0.3)",
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
      {
        label: "Profit",
        data: [],
        borderColor: "green",
        backgroundColor: "rgba(0, 255, 0, 0.3)",
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
    ],
  });

  useEffect(() => {
    (async () => {
      const res = await GET(
        `${getChartRevenue}/?business_id=${currentBusiness}&month=${selectedMonth}&year=${selectedYear}`,
        refreshToken
      );
      if (res.data) {
        if (type === "Service") {
          setData({
            labels:
              selectedMonth === "YTD"
                ? [
                    "Jan",
                    "Feb",
                    "Mar",
                    "Apr",
                    "May",
                    "Jun",
                    "Jul",
                    "Aug",
                    "Sep",
                    "Oct",
                    "Nov",
                    "Dec",
                  ]
                : res.data.service.map((re: any) => re.day),

            datasets: [
              {
                label: "Revenue",
                data: res.data.service.map((re: any) =>
                  convertCentsToDollars(re.total_revenue)
                ),
                borderColor: "orange",
                backgroundColor: "rgba(255, 165, 0, 0.3)",
                fill: true,
                tension: 0.4,
                pointRadius: 5,
                pointHoverRadius: 7,
              },
              {
                label: "Cost",
                data: res.data.service.map((cost: any) =>
                  convertCentsToDollars(cost.total_cost)
                ),
                borderColor: "cyan",
                backgroundColor: "rgba(0, 255, 255, 0.3)",
                fill: true,
                tension: 0.4,
                pointRadius: 5,
                pointHoverRadius: 7,
              },
              {
                label: "Profit",
                data: res.data.service.map((profit: any) =>
                  convertCentsToDollars(profit.profit)
                ),
                borderColor: "green",
                backgroundColor: "rgba(0, 255, 0, 0.3)",
                fill: true,
                tension: 0.4,
                pointRadius: 5,
                pointHoverRadius: 7,
              },
            ],
          });
        } else if (type === "Combined") {
          setData({
            labels:
              selectedMonth === "YTD"
                ? [
                    "Jan",
                    "Feb",
                    "Mar",
                    "Apr",
                    "May",
                    "Jun",
                    "Jul",
                    "Aug",
                    "Sep",
                    "Oct",
                    "Nov",
                    "Dec",
                  ]
                : res.data.combined.map((re: any) => re.day),

            datasets: [
              {
                label: "Revenue",
                data: res.data.combined.map((re: any) =>
                  convertCentsToDollars(re.total_revenue)
                ),
                borderColor: "orange",
                backgroundColor: "rgba(255, 165, 0, 0.3)",
                fill: true,
                tension: 0.4,
                pointRadius: 5,
                pointHoverRadius: 7,
              },
              {
                label: "Cost",
                data: res.data.combined.map((cost: any) =>
                  convertCentsToDollars(cost.total_cost)
                ),
                borderColor: "cyan",
                backgroundColor: "rgba(0, 255, 255, 0.3)",
                fill: true,
                tension: 0.4,
                pointRadius: 5,
                pointHoverRadius: 7,
              },
              {
                label: "Profit",
                data: res.data.combined.map((profit: any) =>
                  convertCentsToDollars(profit.profit)
                ),
                borderColor: "green",
                backgroundColor: "rgba(0, 255, 0, 0.3)",
                fill: true,
                tension: 0.4,
                pointRadius: 5,
                pointHoverRadius: 7,
              },
            ],
          });
        } else if (type === "Alcohol Commission")
          setData({
            labels:
              selectedMonth === "YTD"
                ? [
                    "Jan",
                    "Feb",
                    "Mar",
                    "Apr",
                    "May",
                    "Jun",
                    "Jul",
                    "Aug",
                    "Sep",
                    "Oct",
                    "Nov",
                    "Dec",
                  ]
                : res.data.alcohol.map((re: any) => re.day),

            datasets: [
              // {
              //   label: "Amount",
              //   data: res.data.service.map((re: any) =>
              //     convertCentsToDollars(re.amount)
              //   ),
              //   borderColor: "orange",
              //   backgroundColor: "rgba(255, 165, 0, 0.3)",
              //   fill: true,
              //   tension: 0.4,
              //   pointRadius: 5,
              //   pointHoverRadius: 7,
              // },
              {
                label: "Profit",
                data: res.data.alcohol.map((cost: any) =>
                  convertCentsToDollars(cost.alcoholprofit)
                ),
                borderColor: "#67BD8C",
                backgroundColor: "#67BD8C",
                fill: true,
                tension: 0.4,
                pointRadius: 5,
                pointHoverRadius: 7,
              },
            ],
          });
      }
    })();
    setMonthRang(
      monthsData.filter((m) => {
        if (selectedYear.toString() == new Date().getFullYear().toString()) {
          return (
            m.value <= new Date().getMonth().toString() || m.value === "YTD"
          );
        } else {
          return m
          // return Number(m.value) - 1 || m.value === "YTD";
        }
      })
    );
  }, [type, selectedMonth, currentBusiness, selectedYear]);

  return (
    <div className="bg-white  p-4 rounded-2xl">
      <div className="flex justify-between items-center mb-4 flex-wrap ">
        <h3 className="text-xl font-semibold">Revenue</h3>
        <div className="flex gap-2 flex-wrap mt-2 md:mt-0">
          <select
            className="select-wrapper  text-xs rounded-full cursor-pointer  focus:outline-none  focus:ring-1 focus:ring-primary-red "
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            {RevenueType.map((r: any) => {
              return (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              );
            })}
          </select>
          <select
            className="select-wrapper text-xs rounded-full cursor-pointer  focus:outline-none  focus:ring-1 focus:ring-primary-red"
            onChange={(e) => {
              setSelectedYear(e.target.value);
            }}
            value={selectedYear}
          >
            {yearData.map((r: any) => {
              return (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              );
            })}
          </select>
          <select
            className="select-wrapper  text-xs rounded-full cursor-pointer   focus:outline-none  focus:ring-1 focus:ring-primary-red"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {monthRang.map((r: any) => {
              return (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              );
            })}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-3 justify-center text-xs">
        {data.datasets.map((legend: any, i) => {
          return (
            <div key={i} className="flex gap-1 items-center  justify-center">
              <span>{legend.label}</span>
              <FaCircle
                className={` ${
                  legend.label === "Revenue"
                    ? "text-[#FFA41D]"
                    : legend.label === "Cost"
                    ? "text-[#72C1E2]"
                    : legend.label === "Profit"
                    ? "text-[#67BD8C;]"
                    : legend.label === "Amount"
                    ? "text-primary-red"
                    : ""
                }  mt-1 `}
                size={10}
              />
            </div>
          );
        })}
      </div>
      <Line data={data} options={options} />
    </div>
  );
};

export default RevenueChart;
